import { Map as MapUtils } from '@protector/utils';
import type { Crop } from '@protector/utils/types';
import { slugify } from 'core/utils/strings';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { FieldInfo } from '@features/regional-overview/components/field-details-card/field-details-card.models';
import type { RegionalOverviewV2Properties, RegionalOverviewV2Values } from 'querys/regional-overview/regional-overview.query.models';
import { DATE_FORMAT, EMPTY_VALUE, POPUP_OFFSET } from '../hooks/use-overview-points-popup.constants';

export const parseFeatureValues = (properties: RegionalOverviewV2Properties): RegionalOverviewV2Properties | undefined => {
  if (!properties.values) return undefined;

  try {
    const values = typeof properties.values === 'string' ? (JSON.parse(properties.values) as RegionalOverviewV2Values) : properties.values;
    return { ...properties, values };
  } catch {
    return undefined;
  }
};

export const createCropFromValues = (values: RegionalOverviewV2Values): Crop => ({
  id: values.crop_id,
  name: values.crop_name,
  wk_slug: slugify(values.crop_name)
});

export const createFieldInfoItems = (values: RegionalOverviewV2Values, t: TFunction): FieldInfo[] => [
  { label: t('pages.regional_overview.field_details_card.field_info.crop'), value: t(`general.crops.${values.crop_name}`) },
  { label: t('pages.regional_overview.field_details_card.field_info.variety'), value: values.variety_name },
  {
    label: t('pages.regional_overview.field_details_card.field_info.planting'),
    value: values.planting_date ? dayjs(values.planting_date).format(DATE_FORMAT) : EMPTY_VALUE
  },
  { label: t('pages.regional_overview.field_details_card.field_info.predicted_growth_stage'), value: `BBCH ${values.risks.prevBBCH}` },
  {
    label: t('pages.regional_overview.field_details_card.field_info.last_fungicide_application'),
    value: values.last_fungicide_application ? dayjs(values.last_fungicide_application).format(DATE_FORMAT) : EMPTY_VALUE
  }
];

export type PopupAnchor = MapUtils.PopupAnchor;
export type PopupPosition = MapUtils.PopupPosition;
export type CalculatePopupAnchorParams = Omit<MapUtils.CalculatePopupAnchorParams, 'offsets'>;

export const calculatePopupAnchor = ({
  clickCoordinates,
  containerSize,
  popupSize = { width: 600, height: 400 },
  edgeThreshold = 100
}: CalculatePopupAnchorParams): PopupPosition => {
  return MapUtils.calculatePopupAnchor({
    clickCoordinates,
    containerSize,
    popupSize,
    edgeThreshold,
    offsets: {
      leftCornerVertical: POPUP_OFFSET.LEFT_CORNER_VERTICAL,
      rightTopCornerVertical: POPUP_OFFSET.RIGHT_TOP_CORNER_VERTICAL,
      rightBottomCornerVertical: POPUP_OFFSET.RIGHT_BOTTOM_CORNER_VERTICAL
    }
  });
};
